import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Input, Button } from "@/components/UI";
import { TrendUp, TrendDown, Minus, Sparkle, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";

// Metric labels
const METRICS = [
  { key: "milk_liters", label: "Süt Üretimi", unit: "L", higherIsBetter: true },
  { key: "insemination_animals", label: "Tohumlanan Hayvan", unit: "baş", higherIsBetter: true },
  { key: "pregnancy_rate", label: "Gebelik Oranı", unit: "%", higherIsBetter: true },
  { key: "pregnancy_checked", label: "Bakılan Hayvan", unit: "baş", higherIsBetter: true },
  { key: "treatments", label: "Tedavi Vakası", unit: "adet", higherIsBetter: false },
  { key: "treatment_cost", label: "Tedavi Maliyeti", unit: "₺", higherIsBetter: false },
  { key: "medicine_cost", label: "İlaç Kullanım Gideri", unit: "₺", higherIsBetter: false },
  { key: "stock_cost", label: "Stok Tüketim Gideri", unit: "₺", higherIsBetter: false },
  { key: "income", label: "Gelir", unit: "₺", higherIsBetter: true },
  { key: "expense", label: "Gider", unit: "₺", higherIsBetter: false },
  { key: "net", label: "Net", unit: "₺", higherIsBetter: true },
];

function fmt(v, unit) {
  if (v === undefined || v === null) return "—";
  if (unit === "₺") return `${Number(v).toLocaleString("tr-TR")} ₺`;
  if (unit === "%") return `%${v}`;
  return `${Number(v).toLocaleString("tr-TR")} ${unit || ""}`;
}

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2">{list.map((l, i) => <li key={i}>{renderInline(l)}</li>)}</ul>);
      list = [];
    }
  };
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
  };
  lines.forEach((line, idx) => {
    if (/^###\s/.test(line)) { flushList(); out.push(<h4 key={idx} className="heading text-base mt-4 mb-1">{line.replace(/^###\s/, "")}</h4>); }
    else if (/^##\s/.test(line)) { flushList(); out.push(<h3 key={idx} className="heading text-lg mt-4 mb-2">{line.replace(/^##\s/, "")}</h3>); }
    else if (/^#\s/.test(line)) { flushList(); out.push(<h2 key={idx} className="heading text-xl mt-4 mb-2">{line.replace(/^#\s/, "")}</h2>); }
    else if (/^[-*]\s+/.test(line)) { list.push(line.replace(/^[-*]\s+/, "")); }
    else if (line.trim() === "") { flushList(); out.push(<div key={idx} className="h-2" />); }
    else { flushList(); out.push(<p key={idx} className="my-1">{renderInline(line)}</p>); }
  });
  flushList();
  return out;
}

function AIComment({ period, monthOrYear }) {
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");

  const run = async () => {
    setLoading(true); setComment("");
    try {
      const body = period === "monthly" ? { period, month: monthOrYear } : { period, year: monthOrYear };
      const { data } = await api.post("/ai/comparison-comment", body);
      setComment(data.comment);
    } catch (e) { toast.error(e.response?.data?.detail || "AI hatası"); }
    finally { setLoading(false); }
  };

  return (
    <Card className="bg-[#F7F6F3]" testId={`ai-comment-${period}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" color="#C45B45" />
          <h3 className="heading text-lg">AI Yorumu</h3>
        </div>
        <Button variant="accent" data-testid={`ai-comment-btn-${period}`} onClick={run} disabled={loading}>
          {loading ? "AI analiz ediyor..." : "AI ile Yorumla"}
        </Button>
      </div>
      {!comment && !loading && <p className="text-sm text-[#5e675e]">Buton ile bu dönemin verilerini yapay zeka danışmanına yorumlatın.</p>}
      {loading && <p className="text-sm text-[#8a938c] animate-pulse">Yapay zeka dönem verilerinizi analiz ediyor...</p>}
      {comment && <div className="text-sm border-t border-[#E5E2DC] pt-3 mt-2">{renderMarkdown(comment)}</div>}
    </Card>
  );
}

function DeltaBadge({ delta, higherIsBetter }) {
  if (!delta) return null;
  const isUp = delta.direction === "up";
  const isDown = delta.direction === "down";
  const good = (isUp && higherIsBetter) || (isDown && !higherIsBetter);
  const bad = (isUp && !higherIsBetter) || (isDown && higherIsBetter);
  const color = delta.direction === "flat" ? "#8a938c" : good ? "#2D4A36" : (bad ? "#C45B45" : "#8a938c");
  const bg = delta.direction === "flat" ? "#EEECE5" : good ? "#E4EEE6" : "#FBE9E4";
  const Icon = isUp ? TrendUp : (isDown ? TrendDown : Minus);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: bg, color }}>
      <Icon size={12} weight="bold" /> {delta.pct > 0 ? "+" : ""}{delta.pct}%
    </span>
  );
}

function MetricRow({ label, unit, current, prev, delta, higherIsBetter, prevLabel }) {
  return (
    <tr>
      <td className="font-semibold">{label}</td>
      <td className="text-right tabnum font-semibold">{fmt(current, unit)}</td>
      <td className="text-right tabnum text-[#5e675e]">{fmt(prev, unit)}</td>
      <td className="text-right">
        <DeltaBadge delta={delta} higherIsBetter={higherIsBetter} />
      </td>
    </tr>
  );
}

export default function Comparison() {
  const [tab, setTab] = useState("monthly");
  return (
    <div data-testid="comparison-page">
      <PageHeader title="Karşılaştırma Paneli" subtitle="Aylara ve yıllara göre performans kıyaslaması." />
      <div className="flex flex-wrap gap-1 mb-6 border-b border-[#E5E2DC]">
        <button data-testid="cmp-tab-monthly" onClick={() => setTab("monthly")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "monthly" ? "border-[#1A1D1A] text-[#1A1D1A]" : "border-transparent text-[#6b746c] hover:text-[#1A1D1A]"}`}>
          Aylık Kıyaslama
        </button>
        <button data-testid="cmp-tab-yearly" onClick={() => setTab("yearly")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "yearly" ? "border-[#1A1D1A] text-[#1A1D1A]" : "border-transparent text-[#6b746c] hover:text-[#1A1D1A]"}`}>
          Yıllık Kıyaslama
        </button>
        <button data-testid="cmp-tab-milk" onClick={() => setTab("milk")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "milk" ? "border-[#1A1D1A] text-[#1A1D1A]" : "border-transparent text-[#6b746c] hover:text-[#1A1D1A]"}`}>
          <ChartLineUp size={14} weight="duotone" /> Süt Karşılaştırma
        </button>
      </div>
      {tab === "monthly" && <MonthlyComparison />}
      {tab === "yearly" && <YearlyComparison />}
      {tab === "milk" && <MilkComparisonTab />}
    </div>
  );
}

function MilkComparisonTab() {
  const { user } = useAuth();
  const canSeePrices =
    user?.role === "owner" ||
    user?.role === "manager" ||
    !!user?.visibility?.milk_pricing;
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [view, setView] = useState("stacked"); // stacked | separate | perAnimal

  useEffect(() => {
    api.get(`/milk/comparison?months=${months}`).then(r => setData(r.data)).catch(() => {});
  }, [months]);

  if (!data) return <div className="text-[#8a938c]">Yükleniyor...</div>;
  const hasAny = data.series.some(x => (x.normal_liters + x.antibiotic_liters) > 0);

  return (
    <div className="space-y-6" data-testid="milk-comparison-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-widest text-[#6b746c] font-semibold">Dönem:</label>
        <select data-testid="cmp-milk-months" value={months} onChange={e => setMonths(Number(e.target.value))}
          className="px-3 py-1.5 border border-[#E5E2DC] rounded-md text-sm bg-white">
          <option value={6}>Son 6 ay</option>
          <option value={12}>Son 12 ay</option>
          <option value={24}>Son 24 ay</option>
        </select>
        <div className="flex gap-1 ml-2">
          {[
            { id: "stacked",   label: "Yığın" },
            { id: "separate",  label: "Ayrı Çizgi" },
            { id: "perAnimal", label: "Hayvan/L" },
          ].map(v => (
            <button key={v.id} data-testid={`cmp-milk-view-${v.id}`} onClick={() => setView(v.id)}
              className={`px-3 py-1 text-xs rounded-md ${view === v.id ? "bg-[#1A1D1A] text-white" : "bg-[#F0EEE8] text-[#5e675e]"}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Özet KPI */}
      <div className={`grid gap-3 grid-cols-2 md:grid-cols-${canSeePrices ? 5 : 3}`}>
        <div className="p-4 bg-white border border-[#E5E2DC] rounded" data-testid="cmp-total-liters">
          <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Toplam Süt</div>
          <div className="text-2xl font-bold text-[#1A1D1A] tabnum">{Number(data.stats.total_liters).toLocaleString("tr-TR")}</div>
          <div className="text-xs text-[#8a938c]">Litre</div>
        </div>
        <div className="p-4 bg-[#E4EEE6] border border-[#c2d6c8] rounded">
          <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Normal</div>
          <div className="text-2xl font-bold text-[#2D4A36] tabnum">{Number(data.stats.total_normal_liters).toLocaleString("tr-TR")}</div>
          <div className="text-xs text-[#5e675e]">L · {(100 - data.stats.antibiotic_pct).toFixed(1)}%</div>
        </div>
        <div className="p-4 bg-[#fbeee9] border border-[#f2d4cb] rounded">
          <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Antibiyotikli</div>
          <div className="text-2xl font-bold text-[#8B3A29] tabnum">{Number(data.stats.total_antibiotic_liters).toLocaleString("tr-TR")}</div>
          <div className="text-xs text-[#5e675e]">L · {data.stats.antibiotic_pct}%</div>
        </div>
        {canSeePrices && (
          <>
            <div className="p-4 bg-[#F7EBCC] border border-[#EED7A6] rounded" data-testid="cmp-total-revenue">
              <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Toplam Gelir</div>
              <div className="text-2xl font-bold text-[#8B6914] tabnum">{Number(data.stats.total_revenue).toLocaleString("tr-TR")}</div>
              <div className="text-xs text-[#5e675e]">₺</div>
            </div>
            <div className="p-4 bg-white border border-[#E5E2DC] rounded">
              <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Süt Başı Ort.</div>
              <div className="text-2xl font-bold text-[#1A1D1A] tabnum">
                {data.stats.total_liters > 0 ? (data.stats.total_revenue / data.stats.total_liters).toFixed(2) : "0"}
              </div>
              <div className="text-xs text-[#8a938c]">₺/L</div>
            </div>
          </>
        )}
      </div>

      <Card>
        <h3 className="heading text-lg mb-3">
          {view === "stacked" && "Yığılmış — Toplam Üretim"}
          {view === "separate" && "Ayrı Çizgi — Normal vs Antibiyotikli"}
          {view === "perAnimal" && "Hayvan Başına Ortalama Süt"}
        </h3>
        {!hasAny ? (
          <div className="text-center py-16 text-[#8a938c] italic">Bu dönemde süt üretim kaydı bulunmuyor.</div>
        ) : (
          <div style={{ height: 320 }}>
            <ResponsiveContainer>
              {view === "stacked" && (
                <BarChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEECE5" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(2)} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v) => `${Number(v).toLocaleString("tr-TR")} L`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="normal_liters" stackId="a" fill="#2D4A36" name="Normal Süt" />
                  <Bar dataKey="antibiotic_liters" stackId="a" fill="#C45B45" name="Antibiyotikli Süt" />
                </BarChart>
              )}
              {view === "separate" && (
                <LineChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEECE5" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(2)} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v) => `${Number(v).toLocaleString("tr-TR")} L`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="normal_liters" stroke="#2D4A36" strokeWidth={3} dot={{ r: 3, fill: "#2D4A36" }} name="Normal Süt" />
                  <Line type="monotone" dataKey="antibiotic_liters" stroke="#C45B45" strokeWidth={3} dot={{ r: 3, fill: "#C45B45" }} name="Antibiyotikli Süt" />
                </LineChart>
              )}
              {view === "perAnimal" && (
                <LineChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEECE5" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(2)} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v) => `${v} L/hayvan`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="normal_per_animal" stroke="#2D4A36" strokeWidth={3} dot={{ r: 3, fill: "#2D4A36" }} name="Normal — L/hayvan" />
                  <Line type="monotone" dataKey="antibiotic_per_animal" stroke="#C45B45" strokeWidth={3} dot={{ r: 3, fill: "#C45B45" }} name="Antibiyotikli — L/hayvan" />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Detay tablo */}
      {hasAny && (
        <Card className="!p-0 overflow-hidden">
          <div className="p-4 border-b border-[#EEECE5]">
            <h3 className="heading text-lg">Ay Bazında Detay</h3>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Ay</th>
                <th className="text-right">Normal (L)</th>
                <th className="text-right">Antibiyotikli (L)</th>
                <th className="text-right">Toplam (L)</th>
                {canSeePrices && <th className="text-right">Normal Gelir</th>}
                {canSeePrices && <th className="text-right">Antibiyotikli Gelir</th>}
                {canSeePrices && <th className="text-right">Toplam Gelir</th>}
              </tr>
            </thead>
            <tbody>
              {data.series.slice().reverse().map(row => (
                <tr key={row.month} data-testid={`cmp-milk-row-${row.month}`}>
                  <td className="tabnum font-semibold">{row.month}</td>
                  <td className="text-right tabnum">{row.normal_liters.toLocaleString("tr-TR")}</td>
                  <td className="text-right tabnum text-[#8B3A29]">{row.antibiotic_liters.toLocaleString("tr-TR")}</td>
                  <td className="text-right tabnum font-semibold">{row.total_liters.toLocaleString("tr-TR")}</td>
                  {canSeePrices && <td className="text-right tabnum">{(row.normal_revenue || 0).toLocaleString("tr-TR")} ₺</td>}
                  {canSeePrices && <td className="text-right tabnum text-[#8B3A29]">{(row.antibiotic_revenue || 0).toLocaleString("tr-TR")} ₺</td>}
                  {canSeePrices && <td className="text-right tabnum font-semibold text-[#2D4A36]">{(row.total_revenue || 0).toLocaleString("tr-TR")} ₺</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function MonthlyComparison() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/comparison/monthly?month=${month}`).then(r => setData(r.data)).catch(() => {});
  }, [month]);

  if (!data) return <div className="text-[#8a938c]">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="monthly-comparison">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-widest text-[#6b746c] font-semibold">Ay Seç:</label>
        <Input type="month" data-testid="cmp-month-input" value={month} onChange={e => setMonth(e.target.value)} className="!w-auto" />
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-[#EEECE5]">
          <h3 className="heading text-lg">{data.month} · Önceki ay ({data.previous_month}) karşılaştırması</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Metrik</th>
              <th className="text-right">{data.month}</th>
              <th className="text-right">{data.previous_month}</th>
              <th className="text-right">Değişim</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map(m => (
              <MetricRow key={m.key} label={m.label} unit={m.unit}
                current={data.current[m.key]} prev={data.prev_month[m.key]}
                delta={data.delta_vs_prev_month[m.key]} higherIsBetter={m.higherIsBetter} />
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-[#EEECE5]">
          <h3 className="heading text-lg">{data.month} · Geçen yıl aynı ay ({data.previous_year_month}) karşılaştırması</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Metrik</th>
              <th className="text-right">{data.month}</th>
              <th className="text-right">{data.previous_year_month}</th>
              <th className="text-right">Değişim</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map(m => (
              <MetricRow key={m.key} label={m.label} unit={m.unit}
                current={data.current[m.key]} prev={data.prev_year_month[m.key]}
                delta={data.delta_vs_prev_year[m.key]} higherIsBetter={m.higherIsBetter} />
            ))}
          </tbody>
        </table>
      </Card>

      <AIComment period="monthly" monthOrYear={month} />
    </div>
  );
}

function YearlyComparison() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/comparison/yearly?year=${year}`).then(r => setData(r.data)).catch(() => {});
  }, [year]);

  if (!data) return <div className="text-[#8a938c]">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="yearly-comparison">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-widest text-[#6b746c] font-semibold">Yıl:</label>
        <Input type="number" min="2020" max="2100" data-testid="cmp-year-input" value={year} onChange={e => setYear(Number(e.target.value))} className="!w-32" />
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="p-4 border-b border-[#EEECE5]">
          <h3 className="heading text-lg">{data.year} · {data.previous_year} karşılaştırması</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Metrik</th>
              <th className="text-right">{data.year}</th>
              <th className="text-right">{data.previous_year}</th>
              <th className="text-right">Değişim</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map(m => (
              <MetricRow key={m.key} label={m.label} unit={m.unit}
                current={data.current[m.key]} prev={data.prev_year[m.key]}
                delta={data.delta[m.key]} higherIsBetter={m.higherIsBetter} />
            ))}
          </tbody>
        </table>
      </Card>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Aylık Süt Üretimi ({data.year} vs {data.previous_year})</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data.monthly_series}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8a938c" }} />
                <YAxis tick={{ fontSize: 10, fill: "#8a938c" }} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
                <Legend />
                <Line type="monotone" dataKey="current_milk" stroke="#2D4A36" strokeWidth={2.5} dot={{ r: 3 }} name={`${data.year}`} />
                <Line type="monotone" dataKey="prev_milk" stroke="#8F9E93" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} name={`${data.previous_year}`} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-widest text-[#6b746c] mb-2">Aylık Gelir/Gider ({data.year})</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly_series}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8a938c" }} />
                <YAxis tick={{ fontSize: 10, fill: "#8a938c" }} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v) => `${Number(v).toLocaleString("tr-TR")} ₺`} />
                <Legend />
                <Bar dataKey="current_income" fill="#2D4A36" name="Gelir" />
                <Bar dataKey="current_expense" fill="#C45B45" name="Gider" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <AIComment period="yearly" monthOrYear={year} />
    </div>
  );
}
